/**
 * ProductRowsTable - Main products table component
 *
 * Renders a professional-looking table of products/services with:
 * - Column headers
 * - Draggable rows (when reordering is enabled)
 * - Clean empty state
 *
 * Note: The parent container handles scrolling to avoid nested scroll issues
 * with @hello-pangea/dnd. The Droppable container should not have its own scroll.
 */

import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { Box } from '@mui/material'
import React from 'react'
import ProductRow from '../ContractDialog/ProductRow'
import ProductTableEmpty from './ProductTableEmpty'
import { ProductTableContainer } from './ProductTableStyles'

interface ProductRowsTableProps {
  deal: any
  productRows: any[]
  onDragEnd: (result: DropResult) => void
  onProductChange: (index: number, field: string, value: any, extra?: any) => void
  validationErrors: any
  removeRow: (sk: string) => void
  containerHeight?: number | string
  listRef?: React.RefObject<HTMLDivElement>
  customByServiceKey?: Map<string, { price: number; margin: number }>
  editableFields?: Set<string>
  /** Turns row reordering on. Defaults to true — every current caller renders
   *  this table in edit mode. Pass false for a read-only listing. */
  isReorderable?: boolean
}

const ProductRowsTable = ({
  deal,
  productRows,
  onDragEnd,
  onProductChange,
  validationErrors,
  removeRow,
  containerHeight,
  listRef,
  customByServiceKey,
  editableFields,
  isReorderable = true,
}: ProductRowsTableProps) => {
  const hasProducts = productRows && Array.isArray(productRows) && productRows.length > 0

  const dealType = deal?.type || ''

  // draggableId must be a non-empty string, stable across renders, unique in
  // the list. A brand new row has no SK yet, so fall back to rowId before the
  // index — index-based ids shift mid-drag, so they are the last resort.
  const draggableIdFor = (row: any, index: number) => String(row?.SK || row?.rowId || `draggable-${index}`)

  const renderRow = (row: any, index: number, dragProvided?: any, dragSnapshot?: any) => (
    <ProductRow
      row={row}
      index={index}
      handleProductChange={onProductChange}
      handleRemoveRow={removeRow}
      validationErrors={validationErrors}
      type={dealType}
      isDraft={deal?.isDraft}
      customByServiceKey={customByServiceKey}
      editableFields={editableFields}
      dragProvided={dragProvided}
      dragSnapshot={dragSnapshot}
    />
  )

  const containerSx = { overflowY: 'auto', overflowX: 'hidden', maxHeight: containerHeight } as const

  if (!hasProducts) {
    return (
      <ProductTableContainer ref={listRef} sx={containerSx}>
        <ProductTableEmpty isDraft={deal?.isDraft} />
      </ProductTableContainer>
    )
  }

  if (!isReorderable) {
    return (
      <ProductTableContainer ref={listRef} sx={containerSx}>
        <Box sx={{ pb: 0.5 }}>
          {productRows.map((row, index) => (
            <React.Fragment key={draggableIdFor(row, index)}>{renderRow(row, index)}</React.Fragment>
          ))}
        </Box>
      </ProductTableContainer>
    )
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <ProductTableContainer ref={listRef} sx={containerSx}>
        <Droppable droppableId="product-rows">
          {droppableProvided => (
            <Box ref={droppableProvided.innerRef} {...droppableProvided.droppableProps} sx={{ pb: 0.5 }}>
              {productRows.map((row, index) => {
                const draggableId = draggableIdFor(row, index)
                return (
                  <Draggable key={draggableId} draggableId={draggableId} index={index}>
                    {(dragProvided, dragSnapshot) => renderRow(row, index, dragProvided, dragSnapshot)}
                  </Draggable>
                )
              })}
              {droppableProvided.placeholder}
            </Box>
          )}
        </Droppable>
      </ProductTableContainer>
    </DragDropContext>
  )
}

export default ProductRowsTable
